/**
 * ACWR Thresholds Scaled by Training Age and Experience Level
 * 
 * Research shows that elite/advanced athletes can safely handle higher ACWR
 * than beginners due to better work capacity, recovery, and adaptation.
 * 
 * References:
 * - Gabbett (2016): Elite athletes tolerate higher training loads
 * - Malone et al. (2017): Training age modulates ACWR injury risk
 * - Murray et al. (2017): Elite athletes have different "sweet spot"
 */

export type ExperienceLevel = 'beginner' | 'intermediate' | 'advanced' | 'elite';

export interface ACWRThresholds {
  undertrained: { max: number };
  optimal: { min: number; max: number };
  elevated: { min: number; max: number };
  dangerous: { min: number };
}

/**
 * Get ACWR thresholds based on user's experience level
 */
export function getACWRThresholds(experienceLevel: ExperienceLevel): ACWRThresholds {
  switch (experienceLevel) {
    case 'elite':
      // Elite operators (5+ years tactical training, high work capacity)
      // Can safely handle higher loads, adapted to sustained volume
      return {
        undertrained: { max: 0.7 },
        optimal: { min: 0.7, max: 1.5 },      // Wider optimal range
        elevated: { min: 1.5, max: 1.8 },     // Higher threshold
        dangerous: { min: 1.8 }                // Much higher danger zone
      };
    
    case 'advanced':
      // Advanced (3-5 years consistent training)
      // Good work capacity, can handle moderately higher loads
      return {
        undertrained: { max: 0.75 },
        optimal: { min: 0.75, max: 1.4 },     // Slightly wider range
        elevated: { min: 1.4, max: 1.6 },     // Moderately higher
        dangerous: { min: 1.6 }
      };
    
    case 'intermediate':
      // Intermediate (1-3 years training)
      // Developing work capacity, standard thresholds with slight buffer
      return {
        undertrained: { max: 0.8 },
        optimal: { min: 0.8, max: 1.35 },     // Slight buffer
        elevated: { min: 1.35, max: 1.55 },
        dangerous: { min: 1.55 }
      };
    
    case 'beginner':
    default:
      // Beginners (<1 year training)
      // Lower work capacity, more conservative thresholds
      // Paradoxically can handle HIGHER spikes because low baseline
      return {
        undertrained: { max: 0.8 },
        optimal: { min: 0.8, max: 1.5 },      // Wider range (low baseline)
        elevated: { min: 1.5, max: 1.8 },     // Can handle spikes
        dangerous: { min: 1.8 }                // But still need caution
      };
  }
}

/**
 * Determine ACWR status based on ratio and experience level
 */
export function getACWRStatus(
  ratio: number, 
  experienceLevel: ExperienceLevel
): 'undertrained' | 'optimal' | 'elevated' | 'dangerous' {
  const thresholds = getACWRThresholds(experienceLevel);
  
  if (ratio < thresholds.undertrained.max) {
    return 'undertrained';
  } else if (ratio >= thresholds.optimal.min && ratio <= thresholds.optimal.max) {
    return 'optimal';
  } else if (ratio >= thresholds.elevated.min && ratio <= thresholds.elevated.max) {
    return 'elevated';
  } else {
    return 'dangerous';
  }
}

/**
 * Get contextual explanation for ACWR status
 */
export function getACWRExplanation(
  ratio: number,
  status: 'undertrained' | 'optimal' | 'elevated' | 'dangerous',
  experienceLevel: ExperienceLevel
): string {
  const thresholds = getACWRThresholds(experienceLevel);
  
  switch (status) {
    case 'undertrained':
      return `Your training load has dropped significantly. Consider gradually increasing volume to maintain fitness.`;
    
    case 'optimal':
      if (experienceLevel === 'elite' || experienceLevel === 'advanced') {
        return `Your training load is well-managed for your experience level. Your body is adapted to handle this volume.`;
      } else {
        return `Your training load is in the sweet spot - enough stimulus without excessive fatigue.`;
      }
    
    case 'elevated':
      if (experienceLevel === 'elite') {
        return `Your load is elevated (${ratio.toFixed(2)}), but as an elite operator, you're likely adapted to this. Monitor recovery closely.`;
      } else if (experienceLevel === 'advanced') {
        return `Your load is moderately elevated. You can likely handle this, but watch for signs of overreaching.`;
      } else {
        return `Your training load has increased significantly. Consider a recovery day or reducing volume slightly.`;
      }
    
    case 'dangerous':
      if (experienceLevel === 'elite') {
        return `Even for elite operators, ACWR >${thresholds.dangerous.min.toFixed(1)} indicates very high fatigue. Consider a deload.`;
      } else {
        return `Your training load is dangerously high. Take a recovery day or deload week to prevent injury.`;
      }
  }
}

/**
 * Get recommended action based on ACWR status
 */
export function getACWRRecommendation(
  ratio: number,
  status: 'undertrained' | 'optimal' | 'elevated' | 'dangerous',
  experienceLevel: ExperienceLevel
): string {
  switch (status) {
    case 'undertrained':
      return ' Gradually increase training volume over the next 2-3 weeks';
    
    case 'optimal':
      return ' Continue current training load - well balanced';
    
    case 'elevated':
      if (experienceLevel === 'elite' || experienceLevel === 'advanced') {
        return ' Monitor recovery metrics closely - consider light day if needed';
      } else {
        return ' Reduce volume by 10-20% or add an extra recovery day';
      }
    
    case 'dangerous':
      if (experienceLevel === 'elite') {
        return ' Take 1-2 recovery days, then resume at 80% volume';
      } else {
        return ' Deload week recommended - reduce volume by 40-50%';
      }
  }
}

